import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { auth, db } from '../firebase';
import { ref, get, push, set as dbSet, remove } from 'firebase/database';
import SEO from '../components/SEO';
import { apiFetch } from '../utils/api';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<string, string> = {
    monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
    friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

const PLAN_LIMITS: Record<string, { maxActive: number; maxDays: number; maxContacts: number; label: string }> = {
    free:       { maxActive: 0, maxDays: 0, maxContacts: 0,     label: 'Not available on Free plan' },
    starter:    { maxActive: 1, maxDays: 1, maxContacts: 500,   label: '1 campaign · 1 day/week · 500 contacts' },
    business:   { maxActive: 3, maxDays: 2, maxContacts: 2000,  label: '3 campaigns · 2 days/week · 2,000 contacts' },
    agency:     { maxActive: 0, maxDays: 7, maxContacts: 0,     label: 'Unlimited campaigns · daily' },
    enterprise: { maxActive: 0, maxDays: 7, maxContacts: 0,     label: 'Unlimited campaigns · daily' },
};

interface UserButton { title: string; payload: string; }
interface ListRow    { title: string; payload: string; }
interface ListSection { title: string; rows: ListRow[]; }

interface CampaignMessage {
    type: 'text' | 'image' | 'document' | 'buttons' | 'list';
    text: string;
    subject?: string;
    mediaUrl?: string;
    mediaCaption?: string;
    filename?: string;
    linkUrl?: string;
    userButtons?: UserButton[];
    listSections?: ListSection[];
}

interface CampaignSchedule { days: string[]; time: string; }

interface Campaign {
    id: string;
    name: string;
    status: 'draft' | 'active' | 'paused';
    message: CampaignMessage;
    schedule: CampaignSchedule;
    stats: { sent: number; failed: number };
    createdAt: number;
    lastSentAt?: number;
    lastSentDate?: string;
    unsubscribed?: number;
    targetListId?: string;
}

interface ContactList {
    id: string;
    name: string;
    createdAt: number;
}

interface ListContact {
    id: string;
    name: string;
    phone: string;
    email?: string;
    addedAt: number;
}

const BLANK_MESSAGE: CampaignMessage = {
    type: 'text', text: '', subject: '', mediaUrl: '', mediaCaption: '', filename: '', linkUrl: '',
    userButtons: [{ title: '', payload: '' }],
    listSections: [{ title: 'Options', rows: [{ title: '', payload: '' }] }],
};
const BLANK_CAMPAIGN: Omit<Campaign, 'id' | 'stats' | 'createdAt'> = {
    name: '',
    status: 'draft',
    message: { ...BLANK_MESSAGE },
    schedule: { days: [], time: '09:00' },
    targetListId: undefined,
};

function pad(n: number) { return String(n).padStart(2, '0'); }
function fmtDate(ts?: number) {
    if (!ts) return '—';
    const d = new Date(ts);
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Campaigns() {
    const { botId } = useParams<{ botId: string }>();
    const navigate = useNavigate();

    const [uid, setUid]               = useState('');
    const [userPlan, setUserPlan]     = useState('free');
    const [botType, setBotType]       = useState('whatsapp');
    const [botName, setBotName]       = useState('');
    const [campaigns, setCampaigns]   = useState<Campaign[]>([]);
    const [loading, setLoading]       = useState(true);
    const [saving, setSaving]         = useState(false);
    const [contacts, setContacts]     = useState({ total: 0, eligible: 0 });
    const [selected, setSelected]     = useState<Campaign | null>(null);
    const [isNew, setIsNew]           = useState(false);
    const [form, setForm]             = useState<Omit<Campaign, 'id' | 'stats' | 'createdAt'>>(BLANK_CAMPAIGN);
    const [tab, setTab]               = useState<'message' | 'schedule' | 'stats'>('message');
    const [error, setError]           = useState('');
    const [toast, setToast]           = useState('');

    // Contact list state
    const [panelMode, setPanelMode]         = useState<'campaign' | 'list'>('campaign');
    const [lists, setLists]                 = useState<ContactList[]>([]);
    const [selectedList, setSelectedList]   = useState<ContactList | null>(null);
    const [listContacts, setListContacts]   = useState<ListContact[]>([]);
    const [listLoading, setListLoading]     = useState(false);
    const [listsExpanded, setListsExpanded] = useState(false);
    const [isNewList, setIsNewList]         = useState(false);
    const [newListName, setNewListName]     = useState('');
    const [newContact, setNewContact]       = useState({ name: '', phone: '', email: '' });

    const limits = PLAN_LIMITS[userPlan] || PLAN_LIMITS.free;

    // Days already consumed by OTHER active campaigns (shared weekly budget)
    const otherActiveSendCount = useMemo(() =>
        campaigns
            .filter(c => c.status === 'active' && (isNew || c.id !== selected?.id))
            .reduce((sum, c) => sum + (c.schedule?.days?.length || 0), 0),
        [campaigns, selected, isNew]
    );
    const remainingBudget = limits.maxDays > 0 ? limits.maxDays - otherActiveSendCount : 7;

    // ── Contact list helpers ─────────────────────────────────────────────────
    const loadLists = useCallback(async () => {
        const snap = await get(ref(db, `campaignLists/${botId}`));
        const data = snap.val() || {};
        const arr: ContactList[] = Object.entries(data).map(([id, v]: [string, any]) => ({ id, ...v }));
        setLists(arr.sort((a, b) => b.createdAt - a.createdAt));
    }, [botId]);

    const loadListContacts = useCallback(async (listId: string) => {
        setListLoading(true);
        try {
            const snap = await get(ref(db, `campaignListContacts/${botId}/${listId}`));
            const data = snap.val() || {};
            const arr: ListContact[] = Object.entries(data).map(([id, v]: [string, any]) => ({ id, ...v }));
            setListContacts(arr.sort((a, b) => b.addedAt - a.addedAt));
        } finally {
            setListLoading(false);
        }
    }, [botId]);

    const openList = (list: ContactList) => {
        setSelectedList(list);
        setPanelMode('list');
        setSelected(null);
        setIsNew(false);
        setNewContact({ name: '', phone: '', email: '' });
        loadListContacts(list.id);
    };

    const createList = async () => {
        if (!newListName.trim()) return;
        const newRef = push(ref(db, `campaignLists/${botId}`));
        await dbSet(newRef, { name: newListName.trim(), createdAt: Date.now() });
        setNewListName('');
        setIsNewList(false);
        await loadLists();
        showToast('List created');
    };

    const deleteList = async (list: ContactList) => {
        if (!window.confirm(`Delete "${list.name}" and all its contacts?`)) return;
        await Promise.all([
            remove(ref(db, `campaignLists/${botId}/${list.id}`)),
            remove(ref(db, `campaignListContacts/${botId}/${list.id}`)),
        ]);
        if (selectedList?.id === list.id) { setSelectedList(null); setPanelMode('campaign'); }
        await loadLists();
        showToast('List deleted');
    };

    const addContact = async () => {
        const isEmailBot = botType === 'email';
        const required = isEmailBot ? newContact.email.trim() : newContact.phone.trim();
        if (!required || !selectedList) return;

        if (limits.maxContacts > 0 && listContacts.length >= limits.maxContacts) {
            showToast(`Contact limit reached (${limits.maxContacts.toLocaleString()} on ${userPlan} plan)`);
            return;
        }

        const newRef = push(ref(db, `campaignListContacts/${botId}/${selectedList.id}`));
        await dbSet(newRef, { name: newContact.name.trim(), phone: newContact.phone.trim(), email: newContact.email.trim(), addedAt: Date.now() });
        setNewContact({ name: '', phone: '', email: '' });
        await loadListContacts(selectedList.id);

        // Send opt-in notification for WhatsApp contacts (fire-and-forget)
        if (!isEmailBot && newContact.phone.trim()) {
            apiFetch(`/api/campaigns/${botId}/notify-optin`, {
                method: 'POST',
                body: JSON.stringify({ phone: newContact.phone.trim(), listName: selectedList.name }),
            }).catch(() => {});
        }
    };

    const removeContact = async (contactId: string) => {
        if (!selectedList) return;
        await remove(ref(db, `campaignListContacts/${botId}/${selectedList.id}/${contactId}`));
        setListContacts(prev => prev.filter(c => c.id !== contactId));
    };

    const exportCSV = () => {
        if (!selectedList) return;
        const header = 'Name,Phone,Email';
        const rows = listContacts.map(c =>
            `"${(c.name || '').replace(/"/g, '""')}","${c.phone}","${(c.email || '').replace(/"/g, '""')}"`
        );
        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedList.name.replace(/[^a-z0-9]/gi, '_')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const importCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !selectedList) return;
        e.target.value = '';
        const text = await file.text();
        const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
        // skip header row if detected
        const dataLines = lines[0]?.toLowerCase().replace(/"/g, '').startsWith('name') ? lines.slice(1) : lines;
        let added = 0;
        const currentCount = listContacts.length;
        for (const line of dataLines) {
            if (limits.maxContacts > 0 && currentCount + added >= limits.maxContacts) break;
            // parse quoted CSV fields
            const fields: string[] = [];
            let cur = '', inQ = false;
            for (let i = 0; i < line.length; i++) {
                if (line[i] === '"') { inQ = !inQ; }
                else if (line[i] === ',' && !inQ) { fields.push(cur.trim()); cur = ''; }
                else { cur += line[i]; }
            }
            fields.push(cur.trim());
            const name  = fields[0] || '';
            const phone = fields[1] || fields[0] || '';
            const email = fields[2] || '';
            if (!phone.match(/^\+?\d/)) continue; // skip rows with no valid phone
            const newRef = push(ref(db, `campaignListContacts/${botId}/${selectedList.id}`));
            await dbSet(newRef, { name, phone, email, addedAt: Date.now() });
            added++;
        }
        await loadListContacts(selectedList.id);
        const limitMsg = limits.maxContacts > 0 && currentCount + added >= limits.maxContacts ? ` (limit: ${limits.maxContacts.toLocaleString()})` : '';
        showToast(`Imported ${added} contact${added !== 1 ? 's' : ''}${limitMsg}`);
    };

    const showToast = (msg: string) => {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    };

    // ── Auth + initial load ──────────────────────────────────────────────────
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async user => {
            if (!user) { navigate('/login'); return; }
            setUid(user.uid);
            try {
                const [botSnap, userSnap] = await Promise.all([
                    get(ref(db, `bots/${botId}`)),
                    get(ref(db, `users/${user.uid}`)),
                ]);
                const bot  = botSnap.val();
                const userData = userSnap.val() || {};
                if (!bot || bot.userId !== user.uid || (bot.botType !== 'whatsapp' && bot.botType !== 'email')) {
                    navigate('/dashboard');
                    return;
                }
                setBotType(bot.botType || 'whatsapp');
                setBotName(bot.businessName || (bot.botType === 'email' ? 'Email Bot' : 'WhatsApp Bot'));
                setUserPlan(userData.plan || 'free');
                await Promise.all([loadCampaigns(), loadLists()]);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        });
        return unsub;
    }, [botId, navigate]);

    const loadCampaigns = async () => {
        const res = await apiFetch(`/api/campaigns/${botId}`);
        const data = await res.json();
        console.log('[loadCampaigns] botId:', botId, '| campaigns:', data.campaigns?.map((c: any) => `${c.name}(${c.id})`));
        if (data.success) {
            setCampaigns(data.campaigns || []);
        } else {
            throw new Error(data.error || 'Failed to load campaigns');
        }
    };

    const loadContacts = useCallback(async (campaignId?: string, listId?: string) => {
        const params = new URLSearchParams();
        if (campaignId) params.set('campaignId', campaignId);
        if (listId)     params.set('listId', listId);
        const url = `/api/campaigns/${botId}/contacts/count${params.toString() ? `?${params}` : ''}`;
        const res = await apiFetch(url);
        const data = await res.json();
        if (data.success) setContacts({ total: data.total, eligible: data.eligible });
    }, [botId]);

    // ── Select campaign to edit ──────────────────────────────────────────────
    const selectCampaign = (c: Campaign) => {
        setSelected(c);
        setIsNew(false);
        setPanelMode('campaign');
        setSelectedList(null);
        setForm({
            name: c.name,
            status: c.status,
            message: { ...BLANK_MESSAGE, ...c.message,
                userButtons: c.message?.userButtons?.length ? c.message.userButtons : [{ title: '', payload: '' }],
                listSections: c.message?.listSections?.length ? c.message.listSections : [{ title: 'Options', rows: [{ title: '', payload: '' }] }],
            },
            schedule: { days: c.schedule?.days || [], time: c.schedule?.time || '09:00' },
            targetListId: c.targetListId,
        });
        setTab('message');
        setError('');
        loadContacts(c.id, c.targetListId);
    };

    const startNew = () => {
        setSelected(null);
        setIsNew(true);
        setPanelMode('campaign');
        setSelectedList(null);
        setForm({ ...BLANK_CAMPAIGN, message: { ...BLANK_MESSAGE } });
        setTab('message');
        setError('');
        loadContacts();
    };

    // ── Save ─────────────────────────────────────────────────────────────────
    const save = async () => {
        if (!form.name.trim()) { setError('Campaign name is required'); return; }
        if (botType === 'email' && !form.message.subject?.trim()) { setError('Email subject is required'); return; }
        if (!form.message.text.trim()) { setError('Message body is required'); return; }
        setSaving(true); setError('');
        try {

            const payload = { ...form, targetListId: form.targetListId || null };
            if (isNew) {
                const res = await apiFetch(`/api/campaigns/${botId}`, {
                    method: 'POST',
                    body: JSON.stringify({ campaign: payload }),
                });
                const data = await res.json();
                if (!data.success) { setError(data.error || 'Failed to create'); return; }
                await loadCampaigns();
                // auto-select the newly created campaign so it stays open in the editor
                if (data.campaign) setSelected({ ...data.campaign });
                setIsNew(false);
                showToast('Campaign created');
            } else if (selected) {
                const res = await apiFetch(`/api/campaigns/${botId}/${selected.id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ campaign: payload }),
                });
                const data = await res.json();
                if (!data.success) { setError(data.error || 'Failed to save'); return; }
                await loadCampaigns();
                showToast('Saved');
            }
        } catch (err: any) {
            setError(err.message || 'Something went wrong');
        } finally {
            setSaving(false);
        }
    };

    // ── Delete ───────────────────────────────────────────────────────────────
    const deleteCampaign = async (c: Campaign) => {
        if (!window.confirm(`Delete "${c.name}"?`)) return;
        await apiFetch(`/api/campaigns/${botId}/${c.id}`, { method: 'DELETE' });
        showToast('Deleted');
        if (selected?.id === c.id) { setSelected(null); setIsNew(false); }
        await loadCampaigns();
    };

    // ── Form helpers ─────────────────────────────────────────────────────────
    const setMsg = (patch: Partial<CampaignMessage>) =>
        setForm(f => ({ ...f, message: { ...f.message, ...patch } }));

    const toggleDay = (day: string) => {
        if (form.schedule.days.includes(day)) {
            setForm(f => ({ ...f, schedule: { ...f.schedule, days: f.schedule.days.filter(d => d !== day) } }));
        } else {
            if (limits.maxDays > 0 && form.schedule.days.length >= remainingBudget) return;
            setForm(f => ({ ...f, schedule: { ...f.schedule, days: [...f.schedule.days, day] } }));
        }
    };

    const setBtn = (i: number, patch: Partial<UserButton>) =>
        setMsg({ userButtons: (form.message.userButtons || []).map((b, idx) => idx === i ? { ...b, ...patch } : b) });

    const addBtn = () => {
        if ((form.message.userButtons || []).length >= 2) return;
        setMsg({ userButtons: [...(form.message.userButtons || []), { title: '', payload: '' }] });
    };

    const removeBtn = (i: number) =>
        setMsg({ userButtons: (form.message.userButtons || []).filter((_, idx) => idx !== i) });

    const setRow = (si: number, ri: number, patch: Partial<ListRow>) =>
        setMsg({
            listSections: (form.message.listSections || []).map((s, si2) =>
                si2 === si ? { ...s, rows: s.rows.map((r, ri2) => ri2 === ri ? { ...r, ...patch } : r) } : s
            ),
        });

    const addRow = (si: number) =>
        setMsg({
            listSections: (form.message.listSections || []).map((s, si2) =>
                si2 === si ? { ...s, rows: [...s.rows, { title: '', payload: '' }] } : s
            ),
        });

    const removeRow = (si: number, ri: number) =>
        setMsg({
            listSections: (form.message.listSections || []).map((s, si2) =>
                si2 === si ? { ...s, rows: s.rows.filter((_, ri2) => ri2 !== ri) } : s
            ),
        });

    // ── Styles ───────────────────────────────────────────────────────────────
    const S = {
        page:    { minHeight: '100vh', background: 'var(--bg-deep)', color: 'var(--text-primary)', fontFamily: "'DM Sans', system-ui, sans-serif", display: 'flex', flexDirection: 'column' as const },
        nav:     { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-sidebar)', backdropFilter: 'blur(12px)', position: 'sticky' as const, top: 0, zIndex: 50 },
        main:    { display: 'grid', gridTemplateColumns: '300px 1fr', flex: 1, minHeight: 0 },
        sidebar: { borderRight: '1px solid var(--border-subtle)', padding: '20px', overflowY: 'auto' as const, maxHeight: 'calc(100vh - 57px)' },
        editor:  { padding: '24px', overflowY: 'auto' as const, maxHeight: 'calc(100vh - 57px)' },
        card:    (active: boolean) => ({
            background: active ? 'rgba(91,79,232,0.18)' : 'var(--border-subtle)',
            border: `1px solid ${active ? 'rgba(91,79,232,0.6)' : 'var(--border)'}`,
            borderRadius: '12px', padding: '14px', marginBottom: '10px', cursor: 'pointer',
            transition: 'all 0.15s',
        }),
        input:   { background: 'var(--border-subtle)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 14px', color: 'var(--text-primary)', fontSize: '14px', width: '100%', outline: 'none', boxSizing: 'border-box' as const },
        textarea:{ background: 'var(--border-subtle)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 14px', color: 'var(--text-primary)', fontSize: '14px', width: '100%', outline: 'none', boxSizing: 'border-box' as const, resize: 'vertical' as const, minHeight: '90px' },
        label:   { display: 'block' as const, fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase' as const, letterSpacing: '0.06em' },
        btn:     (color = '#5B4FE8') => ({ background: color, color: 'white', border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }),
        ghost:   { background: 'var(--border-subtle)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
        tab:     (active: boolean) => ({ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, background: active ? '#5B4FE8' : 'transparent', color: active ? 'white' : 'var(--text-muted)', transition: 'all 0.15s' }),
        pill:    (color: string) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: color + '22', color, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }),
        section: { marginBottom: '24px' },
        row:     { display: 'flex', gap: '10px', alignItems: 'flex-end' },
    };

    const statusColor = (s: string) =>
        s === 'active' ? '#10b981' : s === 'paused' ? '#f59e0b' : '#64748b';

    if (loading) return (
        <div style={{ ...S.page, alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ color: 'var(--text-muted)', fontSize: '15px' }}>Loading campaigns...</div>
        </div>
    );

    if (userPlan === 'free') return (
        <div style={{ ...S.page, alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📢</div>
            <h2 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 10px' }}>Campaigns require a paid plan</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>Upgrade to Starter or higher to send mass messages to your contacts.</p>
            <Link to="/billing" style={{ ...S.btn(), textDecoration: 'none', display: 'inline-block' }}>View plans</Link>
        </div>
    );
    const isEmailBot = botType === 'email';

    const isEditing = isNew || selected !== null;
    const activeCampaigns = campaigns.filter(c => c.status === 'active').length;

    return (
        <div style={S.page}>
            <SEO title={`Campaigns — ${botName}`} description="Send mass campaigns to your contacts" keywords="campaigns, mass messaging, email campaigns" type="website" noindex />

            {/* Toast */}
            {toast && (
                <div style={{ position: 'fixed', bottom: '24px', right: '24px', background: '#10b981', color: 'white', padding: '12px 20px', borderRadius: '12px', fontWeight: 600, fontSize: '14px', zIndex: 999, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                    {toast}
                </div>
            )}

            {/* Nav */}
            <nav style={S.nav}>
                <Link to="/dashboard" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '13px' }}>← Dashboard</Link>
                <span style={{ color: 'var(--nav-label)' }}>/</span>
                <span style={{ fontSize: '14px', fontWeight: 700 }}>📢 Campaigns</span>
                <span style={{ fontSize: '13px', color: 'var(--nav-item)', background: 'var(--border-subtle)', padding: '3px 10px', borderRadius: '20px' }}>{botName}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: 'var(--nav-subtle)' }}>{limits.label}</span>
                </div>
            </nav>

            {/* Body */}
            <div style={S.main}>
                {/* ── Sidebar ── */}
                <div style={S.sidebar}>
                    {/* Campaigns section */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--nav-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Campaigns</span>
                        {campaigns.length > 0 && (
                            <span style={{ fontSize: '11px', fontWeight: 700, background: 'rgba(91,79,232,0.25)', color: '#a5b4fc', borderRadius: '10px', padding: '2px 8px' }}>{campaigns.length}</span>
                        )}
                    </div>
                    <button
                        onClick={startNew}
                        disabled={(limits.maxActive > 0 && activeCampaigns >= limits.maxActive)}
                        style={{ ...S.btn(), width: '100%', marginBottom: '12px', opacity: (limits.maxActive > 0 && activeCampaigns >= limits.maxActive) ? 0.5 : 1 }}
                    >
                        + New Campaign
                    </button>

                    {campaigns.length === 0 && !isNew && (
                        <div style={{ textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px', padding: '20px 0' }}>
                            No campaigns yet.
                        </div>
                    )}

                    {campaigns.map(c => (
                        <div key={c.id} style={S.card(selected?.id === c.id && panelMode === 'campaign')} onClick={() => selectCampaign(c)}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontWeight: 700, fontSize: '14px', flex: 1, marginRight: '8px' }}>{c.name || 'Untitled'}</span>
                                <span style={S.pill(statusColor(c.status))}>{c.status}</span>
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--nav-subtle)', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <span>📤 {c.stats?.sent || 0} sent</span>
                                {c.schedule?.days?.length > 0 && <span>📅 {c.schedule.days.map(d => DAY_LABELS[d]).join(', ')}</span>}
                                {c.targetListId && <span>📋 Custom list</span>}
                            </div>
                        </div>
                    ))}

                    {/* Contact Lists section */}
                    <div style={{ marginTop: '24px', borderTop: '1px solid var(--border-subtle)', paddingTop: '16px' }}>
                        <button
                            onClick={() => setListsExpanded(e => !e)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '10px', padding: 0 }}
                        >
                            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--nav-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Contact Lists</span>
                            <span style={{ color: 'var(--nav-item)', fontSize: '12px' }}>{listsExpanded ? '▲' : '▼'} {lists.length}</span>
                        </button>

                        {listsExpanded && (
                            <>
                                {isNewList ? (
                                    <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                                        <input
                                            value={newListName}
                                            onChange={e => setNewListName(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') createList(); if (e.key === 'Escape') setIsNewList(false); }}
                                            placeholder="List name…"
                                            autoFocus
                                            style={{ ...S.input, flex: 1, padding: '7px 10px', fontSize: '13px' }}
                                        />
                                        <button onClick={createList} style={{ ...S.btn(), padding: '7px 12px', fontSize: '12px' }}>✓</button>
                                        <button onClick={() => setIsNewList(false)} style={{ ...S.ghost, padding: '7px 10px', fontSize: '12px' }}>✕</button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setIsNewList(true)}
                                        style={{ ...S.ghost, width: '100%', marginBottom: '10px', fontSize: '12px', padding: '8px' }}
                                    >
                                        + New List
                                    </button>
                                )}

                                {lists.length === 0 && (
                                    <div style={{ fontSize: '12px', color: 'var(--text-faint)', textAlign: 'center', padding: '12px 0' }}>No lists yet</div>
                                )}

                                {lists.map(list => (
                                    <div
                                        key={list.id}
                                        style={{
                                            ...S.card(selectedList?.id === list.id && panelMode === 'list'),
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        }}
                                        onClick={() => openList(list)}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '13px' }}>📋 {list.name}</div>
                                        </div>
                                        <button
                                            onClick={e => { e.stopPropagation(); deleteList(list); }}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', fontSize: '12px', padding: '2px 6px' }}
                                        >✕</button>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </div>

                {/* ── Editor ── */}
                <div style={S.editor}>
                    {/* Contact List editor panel */}
                    {panelMode === 'list' && selectedList && (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' as const }}>
                                <div style={{ fontSize: '22px' }}>📋</div>
                                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>{selectedList.name}</h2>
                                <span style={{ fontSize: '12px', color: limits.maxContacts > 0 && listContacts.length >= limits.maxContacts ? '#ef4444' : 'var(--nav-item)' }}>
                                    {listContacts.length}{limits.maxContacts > 0 ? ` / ${limits.maxContacts.toLocaleString()}` : ''} contacts
                                </span>
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                                    {listContacts.length > 0 && (
                                        <button onClick={exportCSV} style={{ ...S.ghost, fontSize: '12px', padding: '7px 14px' }}>⬇ Export CSV</button>
                                    )}
                                    <label style={{ ...S.ghost, fontSize: '12px', padding: '7px 14px', cursor: 'pointer', display: 'inline-block' }}>
                                        ⬆ Import CSV
                                        <input type="file" accept=".csv,text/csv" onChange={importCSV} style={{ display: 'none' }} />
                                    </label>
                                </div>
                            </div>

                            {/* CSV format hint */}
                            <div style={{ background: 'var(--border-subtle)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '8px 14px', marginBottom: '16px', fontSize: '12px', color: 'var(--nav-subtle)' }}>
                                CSV format: <code style={{ color: 'var(--text-muted)' }}>Name, Phone, Email</code> — header row optional, phone is required (e.g. +263712345678)
                            </div>

                            {/* Add contact form */}
                            <div style={{ background: 'rgba(91,79,232,0.08)', border: '1px solid rgba(91,79,232,0.2)', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Add Contact</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', alignItems: 'flex-end' }}>
                                    <div>
                                        <label style={S.label}>Name</label>
                                        <input value={newContact.name} onChange={e => setNewContact(c => ({ ...c, name: e.target.value }))} placeholder="John Doe" style={S.input} />
                                    </div>
                                    <div>
                                        <label style={S.label}>{isEmailBot ? 'Phone (optional)' : 'Phone *'}</label>
                                        <input value={newContact.phone} onChange={e => setNewContact(c => ({ ...c, phone: e.target.value }))} placeholder="+263712345678" style={S.input} />
                                    </div>
                                    <div>
                                        <label style={S.label}>{isEmailBot ? 'Email *' : 'Email (optional)'}</label>
                                        <input value={newContact.email} onChange={e => setNewContact(c => ({ ...c, email: e.target.value }))} placeholder="john@example.com" style={S.input} />
                                    </div>
                                    <button onClick={addContact} disabled={isEmailBot ? !newContact.email.trim() : !newContact.phone.trim()} style={{ ...S.btn(), padding: '10px 16px', opacity: (isEmailBot ? !newContact.email.trim() : !newContact.phone.trim()) ? 0.5 : 1 }}>Add</button>
                                </div>
                            </div>

                            {/* Contacts table */}
                            {listLoading ? (
                                <div style={{ color: 'var(--text-faint)', padding: '20px', textAlign: 'center' }}>Loading…</div>
                            ) : listContacts.length === 0 ? (
                                <div style={{ textAlign: 'center', color: 'var(--text-faint)', padding: '40px', border: '1px dashed var(--border)', borderRadius: '12px' }}>
                                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>👥</div>
                                    <p style={{ fontSize: '14px' }}>No contacts yet. Add some above.</p>
                                </div>
                            ) : (
                                <div style={{ border: '1px solid var(--border-subtle)', borderRadius: '12px', overflow: 'hidden' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0', background: 'var(--bg-element)', padding: '10px 16px', fontSize: '11px', fontWeight: 700, color: 'var(--nav-item)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        <span>Name</span><span>Phone</span><span>Email</span><span></span>
                                    </div>
                                    {listContacts.map((c, i) => (
                                        <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '0', padding: '12px 16px', borderTop: '1px solid var(--border-subtle)', background: i % 2 === 0 ? 'transparent' : 'var(--border-subtle)', alignItems: 'center' }}>
                                            <span style={{ fontSize: '14px' }}>{c.name || '—'}</span>
                                            <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>{c.phone}</span>
                                            <span style={{ fontSize: '13px', color: 'var(--nav-item)' }}>{c.email || '—'}</span>
                                            <button onClick={() => removeContact(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', fontSize: '14px', padding: '4px 8px' }}>✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Use this list tip */}
                            <div style={{ marginTop: '24px', background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                                💡 To use this list in a campaign, select or create a campaign and set <strong>Target Audience</strong> to this list.
                            </div>
                        </>
                    )}

                    {panelMode === 'campaign' && !isEditing && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', color: 'var(--text-faint)' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📢</div>
                            <p style={{ fontSize: '15px' }}>Select a campaign to edit, or create a new one.</p>
                            <p style={{ fontSize: '13px', marginTop: '8px' }}>Campaigns send to all contacts who have interacted with your bot, or a custom contact list.</p>
                        </div>
                    )}

                    {panelMode === 'campaign' && isEditing && (
                        <>
                            {/* Header */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                                <input
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="Campaign name"
                                    style={{ ...S.input, fontSize: '18px', fontWeight: 700, background: 'transparent', border: 'none', padding: '0', flex: 1, borderBottom: '2px solid var(--border)' }}
                                />
                                <select
                                    value={form.status}
                                    onChange={e => setForm(f => ({ ...f, status: e.target.value as Campaign['status'] }))}
                                    style={{ ...S.input, width: 'auto', padding: '8px 12px' }}
                                >
                                    <option value="draft">Draft</option>
                                    <option value="active">Active</option>
                                    <option value="paused">Paused</option>
                                </select>
                                {!isNew && selected && (
                                    <button onClick={() => deleteCampaign(selected)} style={{ ...S.ghost, color: '#ef4444', borderColor: '#ef4444' }}>Delete</button>
                                )}
                            </div>

                            {/* Tabs */}
                            <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', background: 'var(--bg-element)', borderRadius: '10px', padding: '4px', width: 'fit-content' }}>
                                {(['message', 'schedule', 'stats'] as const).map(t => (
                                    <button key={t} onClick={() => setTab(t)} style={S.tab(tab === t)}>
                                        {t === 'message' ? '✉️ Message' : t === 'schedule' ? '📅 Schedule' : '📊 Stats'}
                                    </button>
                                ))}
                            </div>

                            {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: '10px', padding: '10px 14px', marginBottom: '20px', fontSize: '14px' }}>{error}</div>}

                            {/* ── MESSAGE TAB ── */}
                            {tab === 'message' && (
                                <>
                                    {/* Target Audience */}
                                    <div style={S.section}>
                                        <label style={S.label}>Target Audience</label>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const, marginBottom: '8px' }}>
                                            <button
                                                onClick={() => setForm(f => ({ ...f, targetListId: undefined }))}
                                                style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${!form.targetListId ? '#5B4FE8' : 'var(--border)'}`, background: !form.targetListId ? 'rgba(91,79,232,0.2)' : 'var(--bg-element)', color: !form.targetListId ? '#a5b4fc' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                                            >
                                                👥 All bot contacts
                                            </button>
                                            {lists.map(list => (
                                                <button
                                                    key={list.id}
                                                    onClick={() => setForm(f => ({ ...f, targetListId: list.id }))}
                                                    style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${form.targetListId === list.id ? '#5B4FE8' : 'var(--border)'}`, background: form.targetListId === list.id ? 'rgba(91,79,232,0.2)' : 'var(--bg-element)', color: form.targetListId === list.id ? '#a5b4fc' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                                                >
                                                    📋 {list.name}
                                                </button>
                                            ))}
                                        </div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-faint)' }}>
                                            {form.targetListId
                                                ? `Sends to contacts in the selected list. Create lists in the sidebar.`
                                                : isEmailBot
                                                    ? `Sends to all contacts with an email address (excluding unsubscribed).`
                                                    : `Sends to all contacts who have interacted with your bot (excluding unsubscribed).`}
                                        </div>
                                    </div>

                                    {/* ── Email campaign editor ── */}
                                    {isEmailBot ? (
                                        <>
                                            <div style={S.section}>
                                                <label style={S.label}>Subject <span style={{ color: '#ef4444' }}>*</span></label>
                                                <input
                                                    value={form.message.subject || ''}
                                                    onChange={e => setMsg({ subject: e.target.value })}
                                                    placeholder="e.g. Special offer for you!"
                                                    style={S.input}
                                                />
                                            </div>
                                            <div style={S.section}>
                                                <label style={S.label}>Email Body <span style={{ color: '#ef4444' }}>*</span></label>
                                                <textarea
                                                    value={form.message.text}
                                                    onChange={e => setMsg({ text: e.target.value })}
                                                    placeholder="Write your email campaign body here…"
                                                    style={{ ...S.textarea, minHeight: '160px' }}
                                                />
                                                <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>{form.message.text.length} characters · Plain text — HTML not supported yet</div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                    <div style={S.section}>
                                        <label style={S.label}>Message Type</label>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const }}>
                                            {(['text', 'image', 'document', 'buttons', 'list'] as const).map(t => (
                                                <button key={t} onClick={() => setMsg({ type: t })} style={{ padding: '8px 16px', borderRadius: '8px', border: `1px solid ${form.message.type === t ? '#5B4FE8' : 'var(--border)'}`, background: form.message.type === t ? 'rgba(91,79,232,0.2)' : 'var(--bg-element)', color: form.message.type === t ? '#a5b4fc' : 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                                                    {t === 'text' ? '💬 Text' : t === 'image' ? '🖼️ Image' : t === 'document' ? '📄 Document' : t === 'buttons' ? '🔘 Buttons' : '📋 List'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={S.section}>
                                        <label style={S.label}>Message Body</label>
                                        <textarea
                                            value={form.message.text}
                                            onChange={e => setMsg({ text: e.target.value })}
                                            placeholder={form.message.type === 'image' ? 'Caption text (shown with the image)' : form.message.type === 'document' ? 'Description (shown with the document)' : 'Write your campaign message…'}
                                            style={S.textarea}
                                        />
                                        <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>{form.message.text.length} characters</div>
                                    </div>

                                    {/* Image / Document fields */}
                                    {(form.message.type === 'image' || form.message.type === 'document') && (
                                        <div style={S.section}>
                                            <label style={S.label}>{form.message.type === 'image' ? 'Image URL' : 'Document URL (PDF)'}</label>
                                            <input
                                                value={form.message.mediaUrl || ''}
                                                onChange={e => setMsg({ mediaUrl: e.target.value })}
                                                placeholder="https://..."
                                                style={S.input}
                                            />
                                            {form.message.type === 'document' && (
                                                <div style={{ marginTop: '10px' }}>
                                                    <label style={S.label}>Filename</label>
                                                    <input
                                                        value={form.message.filename || ''}
                                                        onChange={e => setMsg({ filename: e.target.value })}
                                                        placeholder="e.g. brochure.pdf"
                                                        style={S.input}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Link field (for text messages) */}
                                    {form.message.type === 'text' && (
                                        <div style={S.section}>
                                            <label style={S.label}>Link / YouTube URL (optional — appended to message)</label>
                                            <input
                                                value={form.message.linkUrl || ''}
                                                onChange={e => setMsg({ linkUrl: e.target.value })}
                                                placeholder="https://youtube.com/watch?v=... or any link"
                                                style={S.input}
                                            />
                                            <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>WhatsApp shows link previews automatically</div>
                                        </div>
                                    )}

                                    {/* Buttons */}
                                    {form.message.type === 'buttons' && (
                                        <div style={S.section}>
                                            <label style={S.label}>Buttons (max 2 — the Unsubscribe button is added automatically)</label>
                                            {(form.message.userButtons || []).map((btn, i) => (
                                                <div key={i} style={{ ...S.row, marginBottom: '10px' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <label style={{ ...S.label, fontSize: '11px' }}>Button {i + 1} label</label>
                                                        <input value={btn.title} onChange={e => setBtn(i, { title: e.target.value })} placeholder="e.g. Learn more" style={S.input} />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <label style={{ ...S.label, fontSize: '11px' }}>Reply / trigger keyword</label>
                                                        <input value={btn.payload} onChange={e => setBtn(i, { payload: e.target.value })} placeholder="e.g. learn more" style={S.input} />
                                                    </div>
                                                    <button onClick={() => removeBtn(i)} style={{ ...S.ghost, padding: '10px', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)', marginBottom: '0' }}>✕</button>
                                                </div>
                                            ))}
                                            {(form.message.userButtons || []).length < 2 && (
                                                <button onClick={addBtn} style={{ ...S.ghost, fontSize: '12px', padding: '7px 14px' }}>+ Add button</button>
                                            )}
                                            <div style={{ marginTop: '12px', background: 'var(--bg-element)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '20px' }}>🔒</span>
                                                <div>
                                                    <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>Unsubscribe (auto-added)</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-faint)' }}>Always reserved as the last button — removes this contact from future sends</div>
                                                </div>
                                            </div>
                                            <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--nav-subtle)' }}>
                                                Tip: set the reply keyword to a trigger phrase in your Flow Builder (e.g. "main menu") so tapping the button starts a conversation.
                                            </div>
                                        </div>
                                    )}

                                    {/* List */}
                                    {form.message.type === 'list' && (
                                        <div style={S.section}>
                                            <label style={S.label}>List Options (Unsubscribe added automatically)</label>
                                            {(form.message.listSections || []).map((section, si) => (
                                                <div key={si} style={{ background: 'var(--border-subtle)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '14px', marginBottom: '10px' }}>
                                                    <label style={S.label}>Section title</label>
                                                    <input
                                                        value={section.title}
                                                        onChange={e => setMsg({ listSections: (form.message.listSections || []).map((s, si2) => si2 === si ? { ...s, title: e.target.value } : s) })}
                                                        placeholder="Section title"
                                                        style={{ ...S.input, marginBottom: '10px' }}
                                                    />
                                                    {section.rows.map((row, ri) => (
                                                        <div key={ri} style={{ ...S.row, marginBottom: '8px' }}>
                                                            <input value={row.title} onChange={e => setRow(si, ri, { title: e.target.value })} placeholder="Row label" style={S.input} />
                                                            <input value={row.payload} onChange={e => setRow(si, ri, { payload: e.target.value })} placeholder="Trigger keyword" style={S.input} />
                                                            <button onClick={() => removeRow(si, ri)} style={{ ...S.ghost, padding: '10px', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}>✕</button>
                                                        </div>
                                                    ))}
                                                    <button onClick={() => addRow(si)} style={{ ...S.ghost, fontSize: '12px', padding: '6px 12px' }}>+ Add row</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                        </>
                                    )}
                                </>
                            )}

                            {/* ── SCHEDULE TAB ── */}
                            {tab === 'schedule' && (
                                <>
                                    <div style={S.section}>
                                        <label style={S.label}>Send on these days</label>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const }}>
                                            {DAYS.map(day => {
                                                const active = form.schedule.days.includes(day);
                                                const disabled = !active && limits.maxDays > 0 && form.schedule.days.length >= remainingBudget;
                                                return (
                                                    <button
                                                        key={day}
                                                        onClick={() => toggleDay(day)}
                                                        disabled={disabled}
                                                        style={{ padding: '10px 14px', borderRadius: '8px', border: `1px solid ${active ? '#5B4FE8' : 'var(--border)'}`, background: active ? 'rgba(91,79,232,0.25)' : 'var(--bg-element)', color: disabled ? 'var(--text-faint)' : active ? '#a5b4fc' : 'var(--text-secondary)', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px' }}
                                                    >
                                                        {DAY_LABELS[day]}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {limits.maxDays > 0 && (
                                            <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--nav-subtle)' }}>
                                                Weekly send budget: <strong style={{ color: remainingBudget <= 0 ? '#ef4444' : '#a5b4fc' }}>{remainingBudget}</strong> of {limits.maxDays} day(s) remaining
                                                {otherActiveSendCount > 0 && ` (${otherActiveSendCount} used by other campaigns)`}.
                                                <Link to="/billing" style={{ color: '#5B4FE8', marginLeft: '6px', textDecoration: 'none' }}>Upgrade for more →</Link>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ ...S.section, maxWidth: '200px' }}>
                                        <label style={S.label}>Send time (Africa/Harare, 24hr)</label>
                                        <input
                                            type="time"
                                            value={form.schedule.time}
                                            onChange={e => setForm(f => ({ ...f, schedule: { ...f.schedule, time: e.target.value } }))}
                                            style={{ ...S.input, colorScheme: 'dark' }}
                                        />
                                    </div>

                                    <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '10px', padding: '14px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                        ⚡ The campaign scheduler runs every 5 minutes. Your campaign will fire within 5 minutes of the scheduled time, on the selected days.
                                        Set the campaign status to <strong>Active</strong> for it to run automatically.
                                    </div>
                                </>
                            )}

                            {/* ── STATS TAB ── */}
                            {tab === 'stats' && !isNew && selected && (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                                        {[
                                            { label: 'Total sent', value: selected.stats?.sent || 0, color: '#10b981' },
                                            { label: 'Failed', value: selected.stats?.failed || 0, color: '#ef4444' },
                                            { label: 'Unsubscribed', value: selected.unsubscribed || 0, color: '#f59e0b' },
                                        ].map(stat => (
                                            <div key={stat.label} style={{ background: 'var(--bg-element)', border: '1px solid var(--card-border)', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                                                <div style={{ fontSize: '32px', fontWeight: 800, color: stat.color }}>{stat.value}</div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>{stat.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ ...S.section }}>
                                        <label style={S.label}>Eligible recipients now</label>
                                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                            <span style={{ fontSize: '24px', fontWeight: 800 }}>{contacts.eligible}</span>
                                            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>of {contacts.total} total contacts ({(selected.unsubscribed || 0)} unsubscribed)</span>
                                            <button onClick={() => loadContacts(selected.id, selected.targetListId)} style={{ ...S.ghost, fontSize: '12px', padding: '6px 12px' }}>Refresh</button>
                                        </div>
                                    </div>
                                    <div style={S.section}>
                                        <label style={S.label}>Last sent</label>
                                        <div style={{ fontSize: '15px' }}>{fmtDate(selected.lastSentAt)}</div>
                                    </div>
                                    <div style={S.section}>
                                        <label style={S.label}>Schedule</label>
                                        <div style={{ fontSize: '15px' }}>
                                            {selected.schedule?.days?.length > 0
                                                ? `${selected.schedule.days.map(d => DAY_LABELS[d]).join(', ')} at ${selected.schedule.time}`
                                                : 'Not scheduled'}
                                        </div>
                                    </div>
                                </>
                            )}
                            {tab === 'stats' && isNew && (
                                <div style={{ color: 'var(--text-faint)', textAlign: 'center', padding: '40px' }}>Save the campaign first to see stats.</div>
                            )}

                            {/* ── Footer actions ── */}
                            <div style={{ display: 'flex', gap: '10px', marginTop: '32px', borderTop: '1px solid var(--border)', paddingTop: '24px', flexWrap: 'wrap' as const }}>
                                <button onClick={save} disabled={saving} style={S.btn()}>
                                    {saving ? 'Saving...' : isNew ? 'Create Campaign' : 'Save Changes'}
                                </button>
                                <button onClick={() => { setSelected(null); setIsNew(false); }} style={S.ghost}>Cancel</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
